import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class NotificationsController extends Controller {
  static targets = ["badge", "list", "placeholder", "notification"]
  static values = {
    accountId: String, // Current account ID
    accountUnread: Number, // Unread count for the current account
    totalUnread: Number, // Unread count across all the user's accounts
  }

  connect() {
    this.subscription = consumer.subscriptions.create({ channel: "Noticed::NotificationChannel" }, {
      connected: this._connected.bind(this),
      disconnected: this._disconnected.bind(this),
      received: this._received.bind(this)
    })

    if (this.hasUnread()) this.showUnreadBadge()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  _connected() {
    // No-op: required by the ActionCable subscription lifecycle, nothing to sync on connect.
  }

  _disconnected() {
    // No-op: required by the ActionCable subscription lifecycle, nothing to sync on disconnect.
  }

  _received(data) {
    // Ignore if user is signed in to a different account
    if (data.account_id && String(data.account_id) !== this.accountIdValue) {
      return
    }

    this.totalUnreadValue += 1

    if (data.account_id && String(data.account_id) === this.accountIdValue) {
      this.accountUnreadValue += 1
    }

    this.showUnreadBadge()

    // Native notifications trigger a browser notification
    if (data.browser) {
      this.checkPermissionAndNotify(data.browser)
    }
  }

  // Called when the notifications view opens
  open() {
    this.hideUnreadBadge()
    this.markAllAsRead()
  }

  hasUnread() {
    return !!this.accountUnreadValue
  }

  showUnreadBadge() {
    if (!this.hasBadgeTarget) { return }
    this.badgeTarget.classList.remove("hidden")
  }

  hideUnreadBadge() {
    if (!this.hasBadgeTarget) { return }
    this.badgeTarget.classList.add("hidden")
  }

  markAllAsRead() {
    if (!this.subscription || !this.hasNotificationTarget) {
      return
    }

    let ids = this.notificationTargets.map((target) => target.dataset.id)
    
    if (ids.length === 0) {
      return
    }

    this.subscription.perform("mark_as_read", {ids: ids})

    this.accountUnreadValue = 0
    this.totalUnreadValue -= ids.length
  }

  // Browser notifications
  checkPermissionAndNotify(data) {
    // Return if not supported
    if (!("Notification" in globalThis)) return

    if (Notification.permission === "granted") {
      this.browserNotification(data)

    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          this.browserNotification(data)
        }
      })
    }
  }

  browserNotification(data) {
    try {
      new Notification(data.title, data.options)
    } catch (error) {
      console.error('Failed to create browser notification:', error)
    }
  }

  // Automatically sync count to Native apps
  totalUnreadValueChanged() {
    this.syncCountToNative()
  }

  // Automatically sync count to Native apps
  accountUnreadValueChanged() {
    this.syncCountToNative()
  }

  // Update the mobile device with the current unread count
  syncCountToNative() {
    if (globalThis.TurboNativeBridge?.setNotificationCount) {
      globalThis.TurboNativeBridge.setNotificationCount(this.totalUnreadValue, this.accountUnreadValue)
    }
  }
}
